import { ApiError, apiGet, apiSend } from '../api/client'
import { fingerprint, fingerprintKey, fromBase64, open, openBytes, seal, sealBytes, toBase64, type Sealed } from './crypto'

/**
 * The decrypted copy of finance, held in memory while it is unlocked.
 *
 * The server stores sealed rows and nothing else, so everything a screen
 * shows - filters, search, totals, GST by month - is worked out here from
 * what has been decrypted. Nothing decrypted is ever written to disk: not the
 * query cache (finance queries are kept out of what is persisted), not local
 * storage, not the server. Locking throws the key and this copy away.
 */

export type RecordKind = 'client' | 'invoice' | 'payment' | 'tax' | 'gst_payment' | 'settings'

export type Row<T> = {
  id: string
  version: number
  invoice_id: string | null
  created_at: string
  updated_at: string
  value: T
}

type SealedRow = { id: string; sealed: Sealed; version: number; invoice_id: string | null; created_at: string; updated_at: string }

const PATH: Record<RecordKind, string> = {
  client: '/finance/client',
  invoice: '/finance/invoice',
  payment: '/finance/payment',
  tax: '/finance/tax',
  gst_payment: '/finance/gst-payment',
  settings: '/finance/setting',
}

/** How long a decrypted kind is trusted before it is fetched again. Writes made here update it at once. */
const FRESH_MS = 15_000

export class FinanceStore {
  private cache = new Map<RecordKind, { rows: Row<unknown>[]; at: number }>()
  private loading = new Map<RecordKind, Promise<Row<unknown>[]>>()
  private fingerprintKeyPromise: Promise<CryptoKey>

  constructor(readonly dataKey: CryptoKey) {
    this.fingerprintKeyPromise = fingerprintKey(dataKey)
  }

  async rows<T>(kind: RecordKind, force = false): Promise<Row<T>[]> {
    const cached = this.cache.get(kind)

    if (!force && cached && Date.now() - cached.at < FRESH_MS) return cached.rows as Row<T>[]

    let pending = this.loading.get(kind)
    if (!pending) {
      pending = this.fetch(kind).finally(() => this.loading.delete(kind))
      this.loading.set(kind, pending)
    }

    return (await pending) as Row<T>[]
  }

  async one<T>(kind: RecordKind, id: string): Promise<Row<T> | null> {
    return (await this.rows<T>(kind)).find((row) => row.id === id) ?? null
  }

  /**
   * Seals and saves a record. `version` is the one this browser last read, 0
   * for a new record; somebody else having saved in between is a 409 the
   * caller hears about, never a silent overwrite.
   */
  async save<T>(kind: RecordKind, id: string, value: T, version: number, extra: Record<string, unknown> = {}): Promise<number> {
    const sealed = await seal(this.dataKey, value, `${kind}:${id}`)
    const path = kind === 'settings' ? PATH.settings : `${PATH[kind]}/${id}`
    const result = await apiSend<{ version: number }>(path, 'PUT', { sealed, version, ...extra })
    const next = result?.version ?? version + 1

    const cached = this.cache.get(kind)
    if (cached) {
      const now = new Date().toISOString()
      const existing = cached.rows.find((row) => row.id === id)
      const row: Row<unknown> = {
        id,
        version: next,
        invoice_id: (extra.invoice_id as string | undefined) ?? existing?.invoice_id ?? null,
        created_at: existing?.created_at ?? now,
        updated_at: now,
        value,
      }
      cached.rows = existing ? cached.rows.map((r) => (r.id === id ? row : r)) : [...cached.rows, row]
    }

    return next
  }

  async remove(kind: Exclude<RecordKind, 'settings'>, id: string): Promise<void> {
    await apiSend(`${PATH[kind]}/${id}`, 'DELETE')
    const cached = this.cache.get(kind)
    if (cached) cached.rows = cached.rows.filter((row) => row.id !== id)
  }

  /** Forgets one kind so the next read fetches it, e.g. after a conflict. */
  invalidate(kind?: RecordKind): void {
    if (kind) this.cache.delete(kind)
    else this.cache.clear()
  }

  async fingerprint(value: string): Promise<string> {
    return fingerprint(await this.fingerprintKeyPromise, value)
  }

  /* ---- Receipts ------------------------------------------------------------------- */

  async files(): Promise<Array<{ id: string; gst_payment_id: string; name: string; type: string; size: number; created_at: string }>> {
    const listed = await apiGet<Array<{ id: string; gst_payment_id: string; meta: Sealed; size: number; created_at: string }>>('/finance/file')

    return Promise.all(
      listed.map(async (file) => {
        const meta = await open<{ name: string; type: string }>(this.dataKey, file.meta, `file-meta:${file.id}`).catch(() => ({ name: 'Unreadable file', type: 'application/octet-stream' }))

        return { id: file.id, gst_payment_id: file.gst_payment_id, name: meta.name, type: meta.type, size: file.size, created_at: file.created_at }
      }),
    )
  }

  async uploadFile(gstPaymentId: string, file: File): Promise<string> {
    const id = crypto.randomUUID()
    const content = await sealBytes(this.dataKey, new Uint8Array(await file.arrayBuffer()), `file:${id}`)
    const meta = await seal(this.dataKey, { name: file.name, type: file.type || 'application/octet-stream' }, `file-meta:${id}`)

    await apiSend(`/finance/file/${id}`, 'PUT', { gst_payment_id: gstPaymentId, meta, content_nonce: content.nonce, content: content.data })

    return id
  }

  /** The receipt, decrypted, as something the browser can open. */
  async openFile(id: string, type: string): Promise<Blob> {
    const sealed = await apiGet<{ content_nonce: string; content: string }>(`/finance/file/${id}`)
    const bytes = await openBytes(this.dataKey, { nonce: sealed.content_nonce, data: sealed.content }, `file:${id}`)

    return new Blob([bytes], { type })
  }

  async removeFile(id: string): Promise<void> {
    await apiSend(`/finance/file/${id}`, 'DELETE')
  }

  private async fetch(kind: RecordKind): Promise<Row<unknown>[]> {
    let sealedRows: SealedRow[]

    try {
      if (kind === 'settings') {
        const one = await apiGet<SealedRow | null>(PATH.settings)
        sealedRows = one ? [one] : []
      } else {
        sealedRows = await apiGet<SealedRow[]>(PATH[kind])
      }
    } catch (error) {
      // No permission for a kind (GST, say) is an empty list, not a broken page.
      if (error instanceof ApiError && error.status === 403) sealedRows = []
      else throw error
    }

    const rows = await Promise.all(
      sealedRows.map(async (row) => ({
        id: row.id,
        version: row.version,
        invoice_id: row.invoice_id,
        created_at: row.created_at,
        updated_at: row.updated_at,
        value: await open<unknown>(this.dataKey, row.sealed, `${kind}:${row.id}`),
      })),
    )

    this.cache.set(kind, { rows, at: Date.now() })

    return rows
  }
}

/* ---- The one store, set while finance is unlocked --------------------------------------- */

let current: FinanceStore | null = null
const listeners = new Set<() => void>()

export function setFinanceStore(store: FinanceStore | null): void {
  current = store
  listeners.forEach((listener) => listener())
}

export function onFinanceStoreChange(listener: () => void): () => void {
  listeners.add(listener)

  return () => listeners.delete(listener)
}

export function financeStore(): FinanceStore {
  if (!current) throw new FinanceLockedError()

  return current
}

export function financeUnlocked(): boolean {
  return current !== null
}

export class FinanceLockedError extends Error {
  constructor() {
    super('Finance is locked. Unlock it with your finance password.')
  }
}

export { toBase64, fromBase64 }
